/**
 * Table widgets — mock loaders: Competitors' page and content tables, the
 * Influencers roster, Community's ranking tables. Each returns the full list
 * the prototype table built (the tables sort, search and page on the client)
 * — except Top voices and Top performing content, which are paged, sorted
 * (and searched) here, as the API does.
 */
import type { DashboardQuery, WidgetLoaders } from '@/data-sources/contract';
import type {
  CommunityRankingEntry,
  TopPerformingContentData,
  TopPerformingContentParams,
  TopPost,
  TopVoicesData,
  TopVoicesParams,
} from '@/data-sources/contract/widgets/tables';

import { byVoice, inCommunity } from '../engine/cuts';
import { dimensionIndex } from '../engine/dimensions';
import { COMMUNITY_LABELS, VOICE_LABELS, type MentionRow } from '../engine/generator';
import { influencerList, influencerRows, rowsFor, rowsForIndustry } from '../engine/influencers';
import { pageList } from '../engine/pages';
import { postList } from '../engine/posts';
import { rowId } from '../engine/sentimentOverrides';
import { db } from '../db';
import { scoped, slice } from '../query';
import { isOfficialPostMention } from './shared';

/* ------------------------------------------------------------ scopes */

/**
 * Top performing content's rows: Competitors' chip-filtered category, or — on
 * the Influencers detail pages — one creator's rows (optionally one industry),
 * with no chip step, as `InfluencerDetailView` / `IndustryDetailView` sliced them.
 */
export function topContentRows(
  query: DashboardQuery,
  params: TopPerformingContentParams | undefined,
) {
  if (params?.influencer) {
    const creators = influencerRows(slice(query).current);
    return params.industry
      ? rowsForIndustry(creators, params.influencer, params.industry)
      : rowsFor(creators, params.influencer);
  }
  const rows = scoped(query).category;
  /*
   * The sample has no official pages: a brand's placed (seeded) rows stand in
   * for its pages' posts, and the organic rest for the community's.
   */
  if (params?.origin === 'official') return rows.filter((row) => row.origin === 'seeded');
  if (params?.origin === 'community') return rows.filter((row) => row.origin === 'organic');
  /* …and the comments and replies among them for what people said under those posts. */
  if (params?.origin === 'official_post_mentions') return rows.filter(isOfficialPostMention);
  return rows;
}

/* ---------------------------------------------------- community ranking */

/**
 * The prototype's `CommunityRankingTables` `dominant` — the most common value,
 * ties on option order.
 */
function dominant(counts: Map<string, number>, key: 'channel' | 'sentiment'): string {
  let best = '';
  let bestCount = -1;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
      continue;
    }
    if (count !== bestCount) continue;
    const order = dimensionIndex[key];
    if ((order[value] ?? 0) < (order[best] ?? 0)) best = value;
  }
  return best;
}

/* ------------------------------------------------------ server paging */

/**
 * Sort and page a list as a paging API serves it. The sort is the tables'
 * own: ascending compares (stable), descending is that order reversed. A page
 * past the end is clamped to the last.
 */
function servePage<T>(
  items: readonly T[],
  compare: (a: T, b: T) => number,
  direction: 'asc' | 'desc',
  request: { page: number; pageSize: number },
) {
  const ascending = [...items].sort(compare);
  const ordered = direction === 'desc' ? ascending.reverse() : ascending;
  const pageSize = Math.max(1, Math.floor(request.pageSize));
  const lastPage = Math.max(1, Math.ceil(ordered.length / pageSize));
  const page = Math.min(Math.max(1, Math.floor(request.page)), lastPage);
  return {
    items: ordered.slice((page - 1) * pageSize, page * pageSize),
    total: ordered.length,
    page,
    pageSize,
  };
}

/** One page of a community ranking (Top voices). */
export function pageRanking(
  ranked: readonly CommunityRankingEntry[],
  params: TopVoicesParams,
): TopVoicesData {
  const { key, direction } = params.sort;
  const served = servePage(
    ranked,
    (a, b) => (key === 'label' ? a.label.localeCompare(b.label) : a[key] - b[key]),
    direction,
    params,
  );
  return {
    entries: served.items,
    total: served.total,
    page: served.page,
    pageSize: served.pageSize,
    conversationsTotal: ranked.reduce((sum, entry) => sum + entry.conversations, 0),
  };
}

/** One page of Top performing content: the search (post text, any case), then the sort. */
export function pageTopContent(
  ranked: readonly TopPost[],
  params: TopPerformingContentParams,
): TopPerformingContentData {
  const q = params.search?.trim().toLowerCase() ?? '';
  const matching = q === '' ? ranked : ranked.filter((post) => post.text.toLowerCase().includes(q));
  const { key, direction } = params.sort;
  /*
   * Both directions are stable sorts, so equal posts keep the ranked order
   * (newer first) — the default request is then exactly the ranked list.
   */
  const sign = direction === 'desc' ? -1 : 1;
  const served = servePage(matching, (a, b) => sign * (a[key] - b[key]), 'asc', params);
  return { posts: served.items, total: served.total, page: served.page, pageSize: served.pageSize };
}

/** The prototype's `rank`, minus the share (the presenter derives it). */
export function rankCommunity(
  rows: readonly MentionRow[],
  read: (row: MentionRow) => string | null,
  labels: Readonly<Record<string, string>>,
): CommunityRankingEntry[] {
  type Acc = {
    conversations: number;
    engagement: number;
    channels: Map<string, number>;
    sentiments: Map<string, number>;
  };
  const groups = new Map<string, Acc>();
  const bump = (counts: Map<string, number>, value: string) =>
    counts.set(value, (counts.get(value) ?? 0) + 1);

  for (const row of rows) {
    const value = read(row);
    if (value === null) continue;
    let group = groups.get(value);
    if (!group) {
      group = { conversations: 0, engagement: 0, channels: new Map(), sentiments: new Map() };
      groups.set(value, group);
    }
    group.conversations += 1;
    group.engagement += row.engagement;
    bump(group.channels, row.channel);
    bump(group.sentiments, row.sentiment);
  }

  return [...groups.entries()]
    .map(([key, group]) => ({
      key,
      label: labels[key] ?? key,
      channel: dominant(group.channels, 'channel'),
      sentiment: dominant(group.sentiments, 'sentiment'),
      conversations: group.conversations,
      engagement: group.engagement,
    }))
    .sort((a, b) => b.conversations - a.conversations);
}

/* ------------------------------------------------------------ loaders */

export const tablesWidgetLoaders = {
  'competitive-page-performance': async ({ query }) => {
    const rows = scoped(query).category;
    // The sample has no post-to-comment links: a page's mentions are the
    // comments and replies among the brand's placed rows on that channel.
    const mentions = new Map<string, number>();
    for (const row of rows) {
      if (!isOfficialPostMention(row)) continue;
      const key = `${row.brand}|${row.channel}`;
      mentions.set(key, (mentions.get(key) ?? 0) + 1);
    }
    return {
      pages: pageList(rows).map((page) => ({
        brand: page.brand,
        channel: page.channel,
        sentiment: page.sentiment,
        intent: page.intent,
        topic: page.topic,
        posts: page.posts,
        engagement: page.engagement,
        reactions: page.reactions,
        comments: page.comments,
        shares: page.shares,
        mentions: mentions.get(`${page.brand}|${page.channel}`) ?? 0,
      })),
    };
  },

  'top-performing-content': async ({ query, params }) => {
    const rows = topContentRows(query, params);
    const byId = new Map(rows.map((row) => [rowId(row), row]));
    const overrides = db.sentimentOverrides;
    const posts: TopPost[] = postList(rows).map((post) => {
      const row = byId.get(post.id)!;
      return {
        id: post.id,
        text: post.conversation,
        url: post.url,
        brand: post.brand,
        channel: post.channel,
        intents: [row.intent],
        topics: [row.topic],
        sentiment: post.sentiment,
        detectedSentiment: post.detected,
        correctedSentiment: overrides[post.id] ?? null,
        reactions: post.reactions,
        comments: post.comments,
        shares: post.shares,
        engagement: post.interactions,
      };
    });
    return pageTopContent(posts, params);
  },

  'influencer-analytics': async ({ query }) => ({
    influencers: influencerList(influencerRows(slice(query).current)).map((row) => ({
      key: row.id,
      label: row.name,
      platforms: [...row.platforms],
      followers: row.followers,
      posts: row.posts,
      engagementRate: row.engagementRate,
      safety: row.safety,
    })),
  }),

  'top-voices-table': async ({ query, params }) =>
    pageRanking(
      rankCommunity(byVoice(scoped(query).own), (row) => row.voice, VOICE_LABELS),
      params,
    ),

  'top-communities-table': async ({ query }) => ({
    entries: rankCommunity(
      inCommunity(scoped(query).own),
      (row) => row.community,
      COMMUNITY_LABELS,
    ),
  }),
} satisfies Partial<WidgetLoaders>;
